import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Icon } from "@iconify-icon/react";
import { AnimatePresence, motion } from "motion/react";

import { WorkspaceLogoDialog } from "./WorkspaceLogoDialog";

import { cn } from "@/lib/utils";
import useUserStore from "@/features/auth/store/useUserStore";

interface EditableWorkspaceLogoProps {
  logoUrl?: string;
  workspaceName: string;
  className?: string;
}

export const EditableWorkspaceLogo = ({
  logoUrl,
  workspaceName,
  className,
}: EditableWorkspaceLogoProps) => {
  const { t } = useTranslation(["workspaces"]);
  const { user } = useUserStore();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  const canEdit = user?.role === "tutor";

  const handleClick = () => {
    if (canEdit) {
      setIsDialogOpen(true);
    }
  };

  return (
    <>
      <div className={cn("relative", className)}>
        {/* Blurred background behind the actual image */}
        <div className="absolute inset-0 -m-2 rounded-xl opacity-50">
          <img
            alt={workspaceName}
            className="h-full w-full object-cover blur-lg"
            src={logoUrl || "/placeholder.svg?height=40&width=40"}
          />
        </div>

        {/* Actual workspace image */}
        <button
          className={cn(
            "bg-muted/30 relative flex h-12 w-12 items-center justify-center rounded-xl backdrop-blur-sm md:h-20 md:w-20",
            canEdit && "cursor-pointer transition-all duration-200 hover:scale-105",
            !canEdit && "cursor-default",
          )}
          disabled={!canEdit}
          title={canEdit ? t("settings.workspaceInfo.editLogo") : undefined}
          type="button"
          onClick={handleClick}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          <img
            alt={workspaceName}
            className="h-full w-full rounded-xl object-cover"
            src={logoUrl || "/placeholder.svg?height=40&width=40"}
          />

          {/* Pen Icon - Half overflowing */}
          {canEdit && (
            <motion.div
              animate={{ opacity: 1, scale: 1 }}
              className="absolute -top-3 -right-3 z-10"
              initial={{ opacity: 0, scale: 0.8 }}
            >
              <div className="bg-muted/90 border-border/20 flex size-8 items-center justify-center rounded-full border shadow-lg backdrop-blur-sm">
                <Icon className="text-accent-foreground" icon="lucide:edit-2" />
              </div>
            </motion.div>
          )}

          {/* Hover Overlay */}
          <AnimatePresence>
            {canEdit && isHovered && (
              <motion.div
                animate={{ opacity: 1 }}
                className="absolute inset-0 flex items-center justify-center rounded-lg bg-black/60 backdrop-blur-sm"
                exit={{ opacity: 0 }}
                initial={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
              >
                <div className="flex flex-col items-center gap-1">
                  <Icon className="h-4 w-4 text-white md:h-5 md:w-5" icon="lucide:camera" />
                  <span className="text-xs font-medium text-white">
                    {t("settings.workspaceInfo.changeLogo")}
                  </span>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </button>
      </div>

      {/* Logo Dialog */}
      <WorkspaceLogoDialog
        isOpen={isDialogOpen}
        logoUrl={logoUrl}
        workspaceName={workspaceName}
        onClose={() => setIsDialogOpen(false)}
      />
    </>
  );
};
